/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */
// libraries
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { useLazyQuery, useQuery } from '@apollo/client';
import { bindActionCreators } from 'redux';
import QueryString from 'qs';

import {
    string, func, shape, bool, object, number,
} from 'prop-types';
import { compose } from 'recompose';
import mbpLogger from '@module-logger/mbp';
// components
import CategoryPageSkeleton from '../GraphqlSkeletonComponents/CategoryPageSkeleton/CategoryPageSkeleton';
import GraphqlSearchBuilder from './GraphqlSearchBuilder';
// helpers, redux, hooks
import findProductsBySearchTerm from '../../../gql/queries/findProductsBySearchTerm';
import { GRAPHQL_ENV } from '../../../gql';
import { getBrand } from '../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { getFeatureFlags } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { trackEvent as track } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import findContent from '../../../gql/queries/findContent';
import priceRangeFilter from '../../../helpers/priceRangeFilter';
import useCheckedFacets from '../../../helpers/hooks/useCheckedFacets';
import useExperimentServiceAttributes from '../../../helpers/experimentService/useExperimentServiceAttributes';
import findCollectionsBySearchTerm from '../../../gql/queries/findCollectionsBySearchTerm';
import findBlogs from '../../../gql/queries/findBlogs';
import { setSearchRedirectExperiment as setSearchRedirectExperimentAction, setVertexAITrackingData } from '../../../../state/ducks/App/App-Actions';
import useExperimentServiceTracking from '../../../helpers/experimentService/useExperimentServiceTracking';
import cookies from '../../../helpers/common/storage/cookies';
import useSSRMediaQuery from '../../../helpers/hooks/useSSRMediaQuery';

const determineProductOptions = ({
    lowToHighBtn, highToLowBtn, facets,
}, priceRangeData, isFacetFilterPriceEnabled, pageNumber, pageSize = 36, noRedirect, isSearchDidYouMean, getExact, suggestedTerm = null) => {
    let productOptions = {
        orderBy: 'BEST_SELLERS',
        pageSize,
        page: pageNumber,
        noRedirect,
    };

    if (highToLowBtn) {
        productOptions = { ...productOptions, orderBy: 'PRICE_DESC' };
    }
    if (lowToHighBtn) {
        productOptions = { ...productOptions, orderBy: 'PRICE_ASC' };
    }
    if (facets?.length) {
        productOptions = {
            ...productOptions,
        };
        let priceRanges = [];
        let newFacetsData = facets;
        if (isFacetFilterPriceEnabled) {
            // first we checked that value is present or not
            if (typeof priceRangeData !== 'undefined' && priceRangeData?.entries?.length > 0) {
                newFacetsData = [];
                const priceRangeFilterData = priceRangeFilter(facets, priceRangeData);
                newFacetsData = priceRangeFilterData.newFacetsData;
                priceRanges = priceRangeFilterData.priceRanges;
            }
            if (priceRanges.length > 0) {
                productOptions.priceRangeFilter = {
                    fieldName: 'all_prices',
                    ranges: priceRanges,
                };
            }
        }
        if (newFacetsData.length > 0) {
            productOptions.facets = newFacetsData?.filter((data) => !data.includes('='));
        }
    }
    if (isSearchDidYouMean) {
        productOptions = { ...productOptions, useFallbackSearch: getExact };
    }

    if (suggestedTerm) {
        productOptions.suggestedTerm = suggestedTerm;
    }

    return productOptions;
};

const GraphqlSearchContainer = ({
    brand, searchTerm, featureFlags, trackEvent, location, setSearchRedirectExperiment, saveVertexAITrackingData, campaignPageSize, campaignLoading,
}) => {
    /**
     * Getting facets from URL(selected list)
     */
    const device = useSSRMediaQuery();
    const isAggregatorFacetErrorResolver = featureFlags['is-facet-throwing-error-resolver-enabled'];
    const ffIsLoadMoreOptimizationEnabled = featureFlags['is-load-more-optimization-enabled'];
    const defaultProductsToLoad = ffIsLoadMoreOptimizationEnabled ? 36 : 12; // default number of products to load
    const productsToLoadOnLoadMoreButton = device === 'mobile' ? (campaignPageSize?.mobile || defaultProductsToLoad) : (campaignPageSize?.desktop || defaultProductsToLoad); // number of products to load on clicking load more button
    let checkedFacetsData = [];
    let locationData = '';
    const getCheckedFacetsData = useCheckedFacets();
    if (isAggregatorFacetErrorResolver) {
        checkedFacetsData = getCheckedFacetsData;
        locationData = location?.search?.includes('?facet=') ? location.search.replace('?facet=', '') : '';
    } else {
        locationData = location?.search?.includes('?facet=') ? location.search.replace('?facet=', '') : '';
        const redirectLocationData = locationData?.split('&');
        checkedFacetsData = redirectLocationData.length && redirectLocationData?.[0] !== '' ? redirectLocationData : [];
    }

    const facetsDataList = {};
    if (checkedFacetsData.length > 0) {
        facetsDataList.facets = checkedFacetsData;
    }
    const isSearchDidYouMean = featureFlags['search-did-you-mean'];
    const [filterOptions, setFilterOptions] = useState(facetsDataList);
    const [priceFilters, setPriceFilters] = useState([]);
    const [pageCount, setPageCount] = useState(1);
    const [prevProductCount, setPrevProductCount] = useState(0);
    const loadMoreCount = useRef(1);
    const hasMounted = useRef(false);
    const [suggestedTerm, setSuggestedTerm] = useState('');
    const [isSearchQueryLoading, setIsSearchQueryLoading] = useState(false);
    const { targeting, context, isGraphqlTargetingEnabled } = useExperimentServiceAttributes({ queryName: 'findProductsBySearchTerm' });
    const { targeting: collectionGqlTargeting, context: collectionGqlContext, isGraphqlTargetingEnabled: collectionGraphqlTargetingEnabled } = useExperimentServiceAttributes({ queryName: 'findCollectionsBySearchTerm' });
    const SEARCH_PAGE_QUERY = findProductsBySearchTerm(isGraphqlTargetingEnabled, context?.api === 'experiments');
    const SEARCH_PAGE_FACETS_QUERY = findProductsBySearchTerm(isGraphqlTargetingEnabled, context?.api === 'experiments');
    const SEARCH_PAGE_COLLECTION_QUERY = findCollectionsBySearchTerm(collectionGraphqlTargetingEnabled);
    const SEARCH_PAGE_ARTICLE_QUERY = findBlogs();
    const { trackClickStreamExperiments } = useExperimentServiceTracking();
    const newSearchTerm = searchTerm.replace(/[&\\#,-,!,+()$~%.'":*?<>{}]/g, ' '); // replacing special chars allowing and add the space
    const [getSearchFacets, {
        data: searchFacetData, loading: searchFacetLoading,
    }] = useLazyQuery(SEARCH_PAGE_FACETS_QUERY);

    const [getSearchProducts, {
        loading, error, data, fetchMore, refetch, variables,
    }] = useLazyQuery(SEARCH_PAGE_QUERY);

    const [getSearchCollections, {
        data: collectionData,
    }] = useLazyQuery(SEARCH_PAGE_COLLECTION_QUERY);

    const [getSearchArticles, {
        data: articleData,
    }] = useLazyQuery(SEARCH_PAGE_ARTICLE_QUERY);

    // PZ-6966; if there is a redirectUrl in query res, get sre related exp and put in store. need to ensure campaignName or name always contains string 'redirect' when setting up redirect test in SF
    const searchRedirectExperiment = [];
    const queryExperiments = data?.findProductsBySearchTerm?.experiments || [];
    const queryRedirectUrl =  data?.findProductsBySearchTerm?.redirectUrl || null;
    const sreExp = queryExperiments.find((elem) => {
        if (elem?.campaignName?.toLowerCase().includes('redirect') || elem?.name?.toLowerCase().includes('redirect')) {
            return elem;
        }
        return null;
    });

    if (sreExp) {
        searchRedirectExperiment.push(sreExp);
    }
    useEffect(() => {
        setSearchRedirectExperiment({
            experiments: searchRedirectExperiment,
            searchTerm,
            redirectUrl: queryRedirectUrl || '',
        });
    }, [queryRedirectUrl, queryExperiments, searchTerm, setSearchRedirectExperiment]);

    const isSearchFacetFromSeparateQuery = featureFlags['is-search-facet-from-separate-query-enabled'];

    // Read vertex ai tracking data from PWA 5.0 through cookies and clear after saved in redux state
    const completionAttributionToken = cookies.get('completionAttributionToken') || '';
    const selectedPosition = cookies.get('selectedPosition') || '';

    if  (completionAttributionToken && selectedPosition) {
        saveVertexAITrackingData({
            completionAttributionToken,
            selectedPosition,
        });
        cookies.set('completionAttributionToken', '');
        cookies.set('selectedPosition', '');
    }

    useEffect(() => {
        if (isSearchFacetFromSeparateQuery) {
            const [priceRangeData] = (searchFacetData?.findProductsBySearchTerm?.productFacets || [])?.filter((value) => value?.name?.toLowerCase() === 'price range' && value?.entries?.length > 0);
            setPriceFilters(priceRangeData);
        }
    }, [searchFacetData?.findProductsBySearchTerm?.productFacets, isSearchFacetFromSeparateQuery]);

    useEffect(() => {
        if (!isSearchFacetFromSeparateQuery) {
            const [priceRangeData] = (data?.findProductsBySearchTerm?.productFacets || [])?.filter((value) => value?.name?.toLowerCase() === 'price range' && value?.entries?.length > 0);
            setPriceFilters(priceRangeData);
        }
    }, [data?.findProductsBySearchTerm?.productFacets, isSearchFacetFromSeparateQuery]);

    let searchResults = {};
    const isSearchTermBeingABTested = featureFlags['is-search-no-redirect-enabled'];

    const isFacetFilterPriceEnabled = featureFlags['is-facet-filter-price-enabled'];

    const getExact = QueryString.parse(location.search.replace('?', ''))?.exact !== '1';
    useEffect(() => {
        if (isSearchFacetFromSeparateQuery && !campaignLoading) {
            getSearchFacets({
                variables: {
                    brand: brand['domain-name'],
                    term: searchTerm,
                    productOptions: {}, // avoiding facets to update based on options, it only render once based on above search term
                    environment: GRAPHQL_ENV,
                    ...(isGraphqlTargetingEnabled ? { targeting } : {}),
                },
                context,
            });
        }
    }, [campaignLoading]);

    // tracking impression event for experiment service once (edgecase only - if missed from autoSuggest also to save experiments in redux)
    useEffect(() => {
        if (data?.findProductsBySearchTerm?.experiments?.length && typeof window !== 'undefined' && !window.wuAutoSuggestImpressionFired) {
            trackClickStreamExperiments({
                experiments: data.findProductsBySearchTerm.experiments,
                action: 'Impression',
                page: { type: 'search' },
            });
            window.wuAutoSuggestImpressionFired = true;
        }
    }, [data]);

    useEffect(() => {
        if (featureFlags['is-collections-on-search-page-enabled']) {
            getSearchCollections({
                variables: {
                    brand: brand['domain-name'],
                    term: searchTerm,
                    collectionOptions: {
                        pageSize: 4,
                        page: 0,
                    },
                    environment: GRAPHQL_ENV,
                    ...(collectionGraphqlTargetingEnabled ? { targeting: collectionGqlTargeting } : {}),
                },
                context: collectionGqlContext,
            });
        }
    }, []);

    useEffect(() => {
        if (featureFlags['is-articles-on-search-page-enabled']
            && featureFlags['article-brand-contentype-map']
            && featureFlags['article-brand-contentype-map']?.[brand['domain-name']]
        ) {
            getSearchArticles({
                variables: {
                    contentType: `${featureFlags['article-brand-contentype-map'][brand['domain-name']]}_posts`,
                    blogOptions: {
                        keyword: searchTerm,
                        page: 0,
                        pageSize: 4,
                    },
                    environment: GRAPHQL_ENV,
                },
            });
        }
    }, []);

    const fetchMoreResults = (productsToLoad = productsToLoadOnLoadMoreButton) => {
        if (fetchMore) {
            const currentProductCount = data?.findProductsBySearchTerm?.products?.length || 0;
            const nextPage = currentProductCount
                ? Math.ceil(currentProductCount / productsToLoad) + 1
                : pageCount + 1;
            // the below condiitonal is used to mathematically determine the current page.
            // it is necessary to do this because of apollo's caching.
            // if the user searches and paginates then clicks on a product and then goes back to that search
            // then they scroll to paginate again apollo will return all of the previously fetched products as part of that first
            // search query because they are all in apollo's cache.
            // this can lead to an issue where the pageCount is 3 becasue it's after the first pagination call but apollo is returning
            // more products than the first three pages. To prevent this the actual page number is calculated based on number of
            // products that are currently displayed
            setPrevProductCount(currentProductCount);
            setPageCount(nextPage);
            fetchMore({
                variables: {
                    // only need to define the variables that change in between queries
                    productOptions: determineProductOptions(filterOptions, priceFilters, isFacetFilterPriceEnabled, nextPage, productsToLoad, isSearchTermBeingABTested, isSearchDidYouMean, getExact, suggestedTerm),
                },
            });
        }
    };
    // this is use for when I click on the load more button.
    // I will show the next LOAD MORE button after the next 36 products,
    // but I load 12, 12, 12 products three times on scroll down.

    const onLoadMoreClick = (productsToLoad = productsToLoadOnLoadMoreButton, isTrue = false) => {
        if (isTrue) {
            loadMoreCount.current += 1;
        }
        fetchMoreResults(productsToLoad);
    };

    const productFacetsGroup = [];
    const contentType = 'template_search_page';
    const query = `\\"search_parameter\\": \\"${newSearchTerm}\\"`;
    const findGetContentTypeData = {
        brand: brand['domain-name'],
        contentType,
        GRAPHQL_ENV,
        query,
        reference: ['filters.filter_ordering.filters'],
    };

    const defaultQuery = {
        brand: brand['domain-name'],
        contentType,
        GRAPHQL_ENV,
        query: '\\"title\\": \\"Default\\"',
        reference: ['filters.filter_ordering.filters'],
    };

    const Search_Content_QUERY = findContent(findGetContentTypeData);
    /**
     * Getting search template data based on search param from CS
     */
    const { loading: searchLoading, error: searchError, data: searchData } = useQuery(Search_Content_QUERY);

    /**
     * Getting Default entry of search template if flag is on.
     */
    const { data: defaultSearchTemplate } = useQuery(findContent(defaultQuery));
    if (searchData || defaultSearchTemplate) {
        searchResults = searchData?.findContent?.content?.entries?.[0] || defaultSearchTemplate?.findContent?.content?.entries?.[0] || {};
        /**
         * Getting filters based on the search template
         */
        if (isSearchFacetFromSeparateQuery) {
            (searchFacetData?.findProductsBySearchTerm?.productFacets || [])?.filter((facet) => facet?.entries?.length > 0)?.forEach((entries) => {
                productFacetsGroup.push({
                    name: entries.name,
                    entries: entries.entries || [],
                    displayOpen: entries.displayOpen,
                });
            });
        } else {
            (data?.findProductsBySearchTerm?.productFacets || [])?.filter((facet) => facet?.entries?.length > 0)?.forEach((entries) => {
                productFacetsGroup.push({
                    name: entries.name,
                    entries: entries.entries || [],
                    displayOpen: entries.displayOpen,
                });
            });
        }
    }

    // initial search query - wait for campaign data before firing
    useEffect(() => {
        if (!campaignLoading && !hasMounted.current) {
            setPageCount(1);
            setPrevProductCount(0);
            getSearchProducts({
                variables: {
                    brand: brand['domain-name'],
                    term: searchTerm,
                    productOptions: determineProductOptions(filterOptions, priceFilters, isFacetFilterPriceEnabled, 1, productsToLoadOnLoadMoreButton, isSearchTermBeingABTested, isSearchDidYouMean, getExact, suggestedTerm),
                    environment: GRAPHQL_ENV,
                    ...(isGraphqlTargetingEnabled ? { targeting } : {}),
                },
                context,
            });
            hasMounted.current = true;
        }
    }, [campaignLoading]);

    // subsequently when filters change use the refetch query
    useEffect(() => {
        if (hasMounted.current && typeof refetch !== 'undefined') {
            setPageCount(1);
            setPrevProductCount(0);
            setIsSearchQueryLoading(true);
            refetch({
                productOptions: determineProductOptions(filterOptions, priceFilters, isFacetFilterPriceEnabled, 1, productsToLoadOnLoadMoreButton, isSearchTermBeingABTested, isSearchDidYouMean, getExact, suggestedTerm),
                term: searchTerm,
            }).finally(() => {
                setIsSearchQueryLoading(false);
            });
        }
    }, [filterOptions?.facets, searchTerm, getExact]);

    useEffect(() => {
        if (!loading && data?.findProductsBySearchTerm) {
            if (data.findProductsBySearchTerm.totalProducts === 0) {
                trackEvent({
                    eventCategory: 'Search',
                    eventAction: searchTerm,
                    eventLabel: 'No Results for Term',
                });
            }
        }
    }, [data, loading]);

    useEffect(() => {
        const pageData = data?.findProductsBySearchTerm || '';
        if (pageData && featureFlags['search-did-you-mean']) {
            const misSpelledWord = pageData.originalQuery || null;
            const showingResultFor = pageData.showingResultsFor || null;
            if (misSpelledWord && showingResultFor) {
                trackEvent({
                    eventCategory: '<<pageType>>', eventAction: 'Did you mean - shown', eventLabel: `${showingResultFor}|${misSpelledWord}`,
                });
            }
        }
    }, [data, loading]);

    const pageData = data?.findProductsBySearchTerm || '';

    useEffect(() => {
        if (pageData?.showingResultsFor && suggestedTerm !== pageData.showingResultsFor) {
            setSuggestedTerm(pageData.showingResultsFor);
        }
    }, [pageData?.showingResultsFor, suggestedTerm]);

    if (campaignLoading || loading || searchLoading || searchFacetLoading) {
        // return <div style={{ height: '80vh' }} />; // prevent header and footer from snapping together
        return <CategoryPageSkeleton />;
    }

    if (error || searchError) {
        const errorLogObj = {
            appName: process.env.npm_package_name,
            function: 'GraphqlSearchContainer.js',
            message: 'Error loading data from Graphql',
        };
        if (searchError) errorLogObj.searchError = searchError;
        if (error) errorLogObj.error = error;
        if (searchTerm) errorLogObj.searchTerm = searchTerm;

        mbpLogger.logError(errorLogObj);
        return null;
    }

    if (!data) {
        const errorLogObj = {
            appName: process.env.npm_package_name,
            function: 'GraphqlSearchContainer.js',
            message: 'No data returned for query',
        };
        if (searchTerm) errorLogObj.searchTerm = searchTerm;

        mbpLogger.logWarning(errorLogObj);
        return null;
    }

    if (pageData && featureFlags['search-did-you-mean']) {
        const misSpelledWord = pageData.originalQuery || null;
        const canShowResultsFor = pageData.canShowResultsFor || null;
        if (canShowResultsFor && misSpelledWord) {
            trackEvent({
                eventCategory: '<<pageType>>', eventAction: 'Did you mean - submit', eventLabel: `${misSpelledWord}`,
            });
        }
    }

    return (
        <GraphqlSearchBuilder
            key={location.pathname}
            featureFlags={featureFlags}
            fetchMoreResults={onLoadMoreClick}
            loadMoreCount={loadMoreCount.current}
            filterOptions={filterOptions}
            pageCount={pageCount}
            pageData={pageData}
            searchResults={searchResults}
            searchTerm={searchTerm}
            setFilterOptions={setFilterOptions}
            track={trackEvent}
            productFacetsGroup={productFacetsGroup}
            collectionData={collectionData?.findCollectionsBySearchTerm}
            articleData={articleData?.findBlogs}
            variables={variables}
            checkedFacetsData={checkedFacetsData}
            prevProductCount={prevProductCount}
            productsToLoadOnLoadMoreButton={productsToLoadOnLoadMoreButton}
            productCount={pageData?.totalProducts || pageData?.products?.length || 0}
            productLoading={isSearchQueryLoading}
        />
    );
};

GraphqlSearchContainer.propTypes = {
    brand: shape({
        'domain-name': string.isRequired,
    }).isRequired,
    featureFlags: shape({
        'search-did-you-mean': bool,
        'is-collections-on-search-page-enabled': bool,
        'is-articles-on-search-page-enabled': bool,
        'article-brand-contentype-map': object,
    }),
    searchTerm: string,
    trackEvent: func.isRequired,
    setSearchRedirectExperiment: func.isRequired,
    saveVertexAITrackingData: func.isRequired,
    location: shape({
        pathname: string,
    }),
    campaignPageSize: shape({
        mobile: number,
        desktop: number,
    }),
    campaignLoading: bool,
};

GraphqlSearchContainer.defaultProps = {
    searchTerm: '',
    featureFlags: {},
    location: {},
    campaignPageSize: {},
    campaignLoading: false,
};

const mapStateToProps = (state) => ({
    brand: getBrand(state),
    featureFlags: getFeatureFlags(state),
});

const mapDispatchToProps = (dispatch) => ({
    trackEvent: bindActionCreators(track, dispatch),
    setSearchRedirectExperiment: bindActionCreators(setSearchRedirectExperimentAction, dispatch),
    saveVertexAITrackingData: bindActionCreators(setVertexAITrackingData, dispatch),
});

const enhance = compose(
    withRouter,
    connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(GraphqlSearchContainer);
